/**
 * useOnlineMatch — the shared host / join / cancel actions for a game's online
 * challenge panel. `host` and `join` call the trusted callable, then hand
 * off into the `/play` proxy via a full navigation (the proxy is not a React
 * route). On success the page is leaving, so `busy` stays set; on error it clears.
 *
 * A bilateral-ready game (Composition V2: its server admits only an ACCEPTED
 * invitation) hosts with `enterAfterAccept`: the host stays on the game page
 * and `enter`s once the opponent accepts, instead of waiting in the room.
 */
import { useState } from 'react';
import { useServices } from '../services/context.js';
import { onlineMatchUrl } from '../services/playUrl.js';

export interface HostedInvite {
  token: string;
  creationId: string;
  version: number;
}

export interface OnlineMatchActions {
  /** Host a match for a creation → become player 1 and navigate into the room,
   *  or (enterAfterAccept) stay here holding the invite until it is accepted. */
  host: (creationId: string, options: { enterAfterAccept: boolean }) => Promise<void>;
  /** The invite this page hosted and is waiting on (enterAfterAccept only). */
  hosted: HostedInvite | null;
  /** Forget the hosted invite once it ended (cancelled / expired). */
  releaseHosted: () => void;
  /** Navigate into an invite's pinned room. */
  enter: (invite: HostedInvite) => void;
  /** Accept an open invite → become player 2 and navigate into the room. */
  join: (token: string) => Promise<void>;
  /** Withdraw your own open challenge (no navigation). */
  cancel: (token: string) => Promise<void>;
  /** The `host:<id>` / `join:<token>` / `cancel:<token>` key of the in-flight
   *  action, or null — so a caller can spinner-disable just that control. */
  busy: string | null;
  error: string;
}

export function useOnlineMatch(): OnlineMatchActions {
  const services = useServices();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [hosted, setHosted] = useState<HostedInvite | null>(null);

  const enter = ({ creationId, version, token }: HostedInvite): void => {
    window.location.assign(onlineMatchUrl(creationId, version, token));
  };

  const host = async (creationId: string, { enterAfterAccept }: { enterAfterAccept: boolean }): Promise<void> => {
    if (busy) return;
    setBusy(`host:${creationId}`);
    setError('');
    try {
      const r = await services.callable.createMatchInvite({ creationId });
      const invite = { creationId: r.creationId, version: r.version, token: r.token };
      if (enterAfterAccept) {
        setHosted(invite);
        setBusy(null);
      } else {
        enter(invite); // navigates away (player 1 seated first)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not host the match.');
      setBusy(null);
    }
  };

  const join = async (token: string): Promise<void> => {
    if (busy) return;
    setBusy(`join:${token}`);
    setError('');
    try {
      const r = await services.callable.acceptMatchInvite({ token });
      enter({ creationId: r.creationId, version: r.version, token: r.token }); // navigates away (player 2)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not join — it may have just been taken.');
      setBusy(null);
    }
  };

  const cancel = async (token: string): Promise<void> => {
    if (busy) return;
    setBusy(`cancel:${token}`);
    setError('');
    try {
      await services.callable.cancelMatchInvite({ token });
      if (hosted?.token === token) setHosted(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel the challenge.');
    } finally {
      setBusy(null);
    }
  };

  const releaseHosted = (): void => setHosted(null);

  return { host, hosted, releaseHosted, enter, join, cancel, busy, error };
}
